/**
 * Stripe Payment Helper Functions
 *
 * Functions for handling Stripe payment method creation and error handling.
 * Separated for easier testing and maintainability.
 */

import { Stripe, StripeElements, StripeError } from '@stripe/stripe-js';
import { ValidationError } from '../../errors/ValidationError';

export interface PaymentMethodResult {
  paymentMethodId: string;
  error?: string;
}

export interface StripeAvailabilityCheck {
  isAvailable: boolean;
  error?: string;
}

/**
 * Validates that Stripe and Elements are loaded and available
 */
export const validateStripeAvailability = (
  stripe: Stripe | null,
  elements: StripeElements | null,
): StripeAvailabilityCheck => {
  if (!stripe || !elements) {
    return {
      isAvailable: false,
      error: 'Stripe has not loaded yet. Please try again.',
    };
  }

  return {
    isAvailable: true,
  };
};

/**
 * Validates that all required card elements exist
 */
export const validateCardElements = (
  elements: StripeElements,
): { isValid: boolean; error?: string } => {
  const cardNumberElement = elements.getElement('cardNumber');
  const cardExpiryElement = elements.getElement('cardExpiry');
  const cardCvcElement = elements.getElement('cardCvc');

  if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
    return {
      isValid: false,
      error: 'Card elements not found',
    };
  }

  return {
    isValid: true,
  };
};

/**
 * Creates a Stripe payment method
 * Assumes validation has already been performed
 */
export const createStripePaymentMethod = async (
  stripe: Stripe,
  elements: StripeElements,
  zipCode: string,
): Promise<PaymentMethodResult> => {
  try {
    // Validate zip code is provided - USER VALIDATION ERROR
    if (!zipCode || zipCode.trim() === '') {
      throw new ValidationError('Zip code is required');
    }

    // Validate Stripe availability
    const availabilityCheck = validateStripeAvailability(stripe, elements);
    if (!availabilityCheck.isAvailable) {
      throw new Error(availabilityCheck.error);
    }

    // Validate card elements exist
    const elementsCheck = validateCardElements(elements);
    if (!elementsCheck.isValid) {
      throw new Error(elementsCheck.error);
    }

    const cardNumberElement = elements.getElement('cardNumber');

    // Create payment method
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumberElement!,
      billing_details: {
        address: {
          postal_code: zipCode,
        },
      },
    });

    // Handle Stripe API errors
    if (error) {
      const stripeError = error as StripeError;
      const errorMsg =
        stripeError.message || 'An error occurred processing your payment';
      throw new Error(errorMsg);
    }

    // Handle missing payment method (shouldn't happen, but defensive)
    if (!paymentMethod) {
      throw new Error('Failed to create payment method');
    }

    return {
      paymentMethodId: paymentMethod.id,
    };
  } catch (error) {
    // Re-throw ValidationError as-is to preserve error type
    // This allows caller to distinguish user errors from system errors
    if (error instanceof ValidationError) {
      throw error;
    }

    const errorMsg =
      error instanceof Error
        ? error.message
        : 'An error occurred processing your request';

    return {
      paymentMethodId: '',
      error: errorMsg,
    };
  }
};

/**
 * Formats Stripe error for display to user
 */
export const formatStripeError = (error: StripeError): string => {
  return error.message || 'An error occurred processing your payment';
};
